import { workspaceTextFor, type WorkspaceText } from './interface-copy';
import { useWorkspaceText } from './interface-copy';
import { useEffect, useState } from 'react';
import { useWorkspace } from './context';
import { DeleteLibraryItem } from './item-archive/delete-item';
import { downloadText } from './api';
import { WorkspaceIcon } from './icons';
import { dateLabel } from './primitives';
import { csvText, WorkspaceChart, WorkspaceTable } from './table';
import type { CompanyResult, WorkspaceTurnContext } from './types';
import { emptyTurnContext } from './types';
import { lineageDetail, resultLineage, revisionDate, workspaceRead } from './result-presentation';
import type { ResultLineage } from './result-presentation';
import { DeckView } from './deck/deck-view';
import { ResultPreview, resultViews } from './result-preview/result-preview';
import { audienceLabel } from './sharing-api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import workspaceFeedbackCopy from '@/i18n/ns/workspace-feedback';
import {
  LivingResultActions,
  ResultEditor,
  ResultHistoryList,
  livingEyebrow,
} from './living-result';
import { ProvenancePanel } from './provenance-panel';
import { CanvasActivityTab } from './canvas-activity-tab';
import type { CanvasTarget } from './types';
import { ResultPage } from './page-frame/result-page';

function AdHocNextStep({
  result,
  context,
}: {
  result: CompanyResult;
  context: WorkspaceTurnContext;
}) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const feedback = useNamespaceCopy(workspaceFeedbackCopy);
  const ws = useWorkspace();
  return (
    <div className="ws-next-step">
      <h2>{uiText('Keep the work moving.')}</h2>
      <p>
        {uiText(
          'Ask a follow-up with this result in view, or turn a useful approach into a company Skill.',
        )}
      </p>
      <div className="ws-actions">
        <button
          className="ws-text-button"
          onClick={() =>
            ws.send(formatCopy(feedback.resultSkillPrompt, { title: result.title }), {
              ...context,
              intent: { mode: 'skill-author' },
            })
          }
        >
          {uiText('Save approach as a Skill')}
        </button>
      </div>
    </div>
  );
}

/** The result came from a Skill this workspace can open: name it and offer the
 * work that follows from it, never "save this approach as a Skill" again. */
function SkillNextStep({
  lineage,
}: {
  lineage: ResultLineage & { skill: NonNullable<ResultLineage['skill']> };
}) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const feedback = useNamespaceCopy(workspaceFeedbackCopy);
  const ws = useWorkspace();
  const { skill } = lineage;
  const preview = lineage.state === 'draft-preview';
  // How the result was made is a fact about then; whether the Skill can be used
  // is a fact about the Skill now. A draft previewed back then may have been
  // published since, and then it is opened and used like any published Skill.
  const drafting = preview && !skill.published;
  const made = preview
    ? formatCopy(feedback.draftResultLineage, { version: lineage.version })
    : formatCopy(feedback.skillResultLineage, { version: lineage.version });
  const next = drafting
    ? uiText('The draft is not published yet, so no one else follows it.')
    : preview
      ? formatCopy(feedback.publishedSkillResultLineage, { name: lineage.name })
      : uiText('Use it in a chat for current data, or refine the Skill first.');
  return (
    <div className="ws-next-step">
      <h2>
        {preview ? uiText('Preview of') : uiText('Made with')} {lineage.name}.
      </h2>
      <p>
        {made} {next}
      </p>
      <div className="ws-actions">
        <button
          className="ws-text-button"
          onClick={() => ws.openCanvas({ kind: 'skill', id: skill.id })}
        >
          {drafting ? uiText('Open the draft') : uiText('Open Skill')}
        </button>
        {!drafting && (
          <button className="ws-text-button" onClick={() => ws.useSkill(skill)}>
            {uiText('Use in chat')}
          </button>
        )}
        <button className="ws-text-button" onClick={() => ws.authorSkill(skill)}>
          {drafting ? uiText('Finish the draft') : uiText('Refine this Skill')}
        </button>
      </div>
    </div>
  );
}

/** The workspace could not be read, so whether its Skill is still there is
 * unknown. Say that, and offer the same retry the Library page offers. */
function UnreadNextStep() {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const ws = useWorkspace();
  return (
    <div className="ws-next-step">
      <h2>{uiText('Made with a Skill.')}</h2>
      <p>
        {uiText(
          'This workspace could not be loaded just now, so that Skill cannot be opened yet. The result itself is unchanged.',
        )}
      </p>
      <div className="ws-actions">
        <button className="ws-text-button" onClick={() => void ws.refresh()}>
          {uiRich('Try again {value1}', { value1: <WorkspaceIcon name="arrow" size={14} /> })}
        </button>
      </div>
    </div>
  );
}

function ResultNextStep({
  result,
  lineage,
  context,
}: {
  result: CompanyResult;
  lineage: ResultLineage;
  context: WorkspaceTurnContext;
}) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  if (lineage.state === 'ad-hoc') return <AdHocNextStep result={result} context={context} />;
  if (lineage.skill) return <SkillNextStep lineage={{ ...lineage, skill: lineage.skill }} />;
  // The workspace has not been read yet, so which Skill made this result is
  // still unknown: offer nothing rather than a claim we cannot support.
  if (lineage.state === 'pending') return null;
  if (lineage.state === 'unread') return <UnreadNextStep />;
  return (
    <div className="ws-next-step">
      <h2>{uiText('Made with a Skill.')}</h2>
      <p>
        {uiText(
          'That Skill is not available in this workspace, so it cannot be opened or used again. The result itself is unchanged.',
        )}
      </p>
    </div>
  );
}

// The line under the title: what this person can do with the Result.
function resultDescription(result: CompanyResult, uiText: WorkspaceText = workspaceTextFor('en')) {
  if (result.maintainedBy)
    return uiText('An agent keeps this up to date. Continue the work in chat.');
  return result.canEdit
    ? uiText('Edit it here, or continue the work in chat.')
    : uiText('Duplicate it to your Library, or continue the work in chat.');
}

// "Result · Everyone at Fjällbo · Version 4 · Edited by Maria, 2 h ago". A
// revision keeps this result's id, so a link made before it still lands
// here; whoever followed that link sees which version they are reading. A
// Result read without its revision label still says when it moved.
function ResultEyebrow({ result }: { result: CompanyResult }) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const ws = useWorkspace();
  const label = livingEyebrow(result, ws.user?.id, uiText);
  const revisedAt = revisionDate(result);
  return (
    <div className="ws-eyebrow">
      {uiRich('Result · {value1} {value2}', {
        value1: audienceLabel(result, ws.companyName, locale),
        value2: label ? (
          <> · {label}</>
        ) : (
          revisedAt && (
            <>
              {' '}
              {uiText('· Revised')}
              {dateLabel(revisedAt, uiText.locale)}
            </>
          )
        ),
      })}
    </div>
  );
}

export function ResultCanvas({ result, target }: { result: CompanyResult; target: CanvasTarget }) {
  const { text: uiText, rich: uiRich, locale } = useWorkspaceText();
  const ws = useWorkspace();
  // Living Results (collaboration v1): editing, History and provenance.
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    ws.setCanvasDirty(editing);
    // Avoid scheduling a provider update while an already-clean canvas is
    // being unmounted (the workspace test runner mounts many canvases in one
    // root). An active edit still clears when its canvas is replaced.
    return editing ? () => ws.setCanvasDirty(false) : undefined;
  }, [editing]);
  const payload = result.payload;
  const { markdown, deck, chart, table } = resultViews(result.kind, payload);
  const defaultTab = table && !chart ? 'table' : 'overview';
  const availableTabs = new Set([
    'overview',
    ...(table ? ['table'] : []),
    ...(chart ? ['chart'] : []),
    'history',
    'sources',
    ...(target.activityMessageId ? ['activity'] : []),
  ]);
  const tab = target.tab && availableTabs.has(target.tab) ? target.tab : defaultTab;
  const lineage = resultLineage(result, ws.data.skills, workspaceRead(ws.loading, ws.error));
  const context = {
    ...emptyTurnContext(),
    resultIds: [result.id],
  };
  const downloadLabel = markdown !== null ? uiText('Download Markdown') : uiText('Download CSV');
  const slideCount = deck?.slides.length ?? 0;
  const revisedAt = revisionDate(result);
  const download = () => {
    if (markdown !== null) downloadText(result.title + '.md', markdown, 'text/markdown');
    else if (table) downloadText(result.title + '.csv', csvText(table), 'text/csv');
  };
  return (
    <div className="ws-result-canvas">
      <div className="ws-canvas-body ws-canvas-heading">
        <ResultEyebrow result={result} />
        <h1>{result.title}</h1>
        <p className="ws-result-description">
          {deck
            ? uiText(slideCount === 1 ? '{count} slide' : '{count} slides', { count: slideCount }) +
              ' · '
            : table
              ? uiText('{value1} rows ·', { value1: table.rows.length }) + ' '
              : ''}
          {resultDescription(result, uiText)}
        </p>
        <div className="ws-actions ws-result-actions">
          <DeleteLibraryItem
            kind="result"
            id={result.id}
            title={result.title}
            canDelete={result.canDelete}
          />
          <LivingResultActions
            result={result}
            context={context}
            onEdit={() => {
              ws.openCanvas({ ...target, tab: 'overview' });
              setEditing(true);
            }}
          />
          {/* A deck carries its own download beside the stage, where the
              PowerPoint it produces is what the button plainly means. */}
          {!deck && result.kind !== 'html' && (
            <button
              className="ws-button"
              aria-label={downloadLabel}
              title={downloadLabel}
              disabled={markdown === null && !table}
              onClick={download}
            >
              {uiRich('{value1} Download', { value1: <WorkspaceIcon name="download" size={15} /> })}
            </button>
          )}
          <button
            className="ws-text-button"
            onClick={() =>
              // navigator.clipboard is undefined on insecure (http LAN) origins.
              void Promise.resolve()
                .then(() => navigator.clipboard.writeText(location.href))
                .then(() => ws.notify(uiText('Result link copied.')))
                .catch(() =>
                  ws.notify(
                    uiText('Could not copy the link. Use your browser address bar.'),
                    null,
                    'error',
                  ),
                )
            }
          >
            {uiRich('{value1} Copy link', { value1: <WorkspaceIcon name="source" size={13} /> })}
          </button>
        </div>
      </div>
      <div className="ws-view-tabs ws-canvas-tabs">
        {[
          'overview',
          ...(table ? ['table'] : []),
          ...(chart ? ['chart'] : []),
          'history',
          'sources',
          ...(target.activityMessageId ? ['activity'] : []),
        ].map((name) => (
          <button
            className={name === tab ? 'active' : ''}
            aria-pressed={name === tab}
            key={name}
            onClick={() =>
              ws.openCanvas({
                ...target,
                tab: name,
                ...(name === 'activity' && !target.activityTab ? { activityTab: 'steps' } : {}),
              })
            }
          >
            {name[0].toUpperCase() + name.slice(1)}
            {name === 'history' && result.revision && (
              <span className="ws-tab-count">{result.revision.current}</span>
            )}
          </button>
        ))}
      </div>
      <div className="ws-canvas-body">
        {tab === 'activity' ? (
          <CanvasActivityTab target={target} />
        ) : tab === 'overview' && editing && markdown !== null ? (
          <ResultEditor result={result} markdown={markdown} onClose={() => setEditing(false)} />
        ) : tab === 'overview' ? (
          <>
            {result.kind === 'html' ? (
              <ResultPage result={result} full={Boolean(target.full)} />
            ) : deck ? (
              <DeckView result={result} deck={deck} />
            ) : markdown !== null || chart || table ? (
              <ResultPreview kind={result.kind} payload={payload} />
            ) : (
              <p className="ws-muted">{uiText('No preview is available for this result.')}</p>
            )}
            {table && (
              <p className="ws-page-note">
                {uiRich(
                  '{value1} rows are retained in this snapshot. Open Table to inspect the values.',
                  { value1: table.rows.length },
                )}
              </p>
            )}
            {result.maintainedBy && (
              <p className="ws-result-maintained-note">
                {uiText(
                  'People can’t edit a Result an agent keeps up to date; the next refresh would replace their edit. Change the agent’s instructions instead.',
                )}
              </p>
            )}
            <ProvenancePanel kind="result" id={result.id} />
            <ResultNextStep result={result} lineage={lineage} context={context} />
          </>
        ) : tab === 'history' ? (
          <ResultHistoryList result={result} />
        ) : tab === 'table' && table ? (
          <WorkspaceTable table={table} />
        ) : tab === 'chart' && chart ? (
          <WorkspaceChart chart={chart} columns={table?.columns} />
        ) : tab === 'sources' ? (
          <>
            <h2>{uiText('Linked sources')}</h2>
            {result.sourceIds.length ? (
              result.sourceIds.map((id) => (
                <button
                  className="ws-source-link"
                  key={id}
                  onClick={() => ws.openCanvas({ kind: 'source', id })}
                >
                  <WorkspaceIcon name="source" size={16} />
                  <span>
                    {ws.data.sources.find((source) => source.id === id)?.title ??
                      uiText('Saved source')}
                  </span>
                  <WorkspaceIcon name="arrow" size={14} />
                </button>
              ))
            ) : (
              <p className="ws-muted">
                {uiText(
                  'No source documents are linked to this result. Company knowledge and other references may be cited in the answer.',
                )}
              </p>
            )}
            <h2 className="ws-run-heading">{uiText('Run details')}</h2>
            <dl className="ws-run-details">
              <dt>{uiText('Created')}</dt>
              <dd>
                {dateLabel(result.createdAt, uiText.locale)}
                {revisedAt
                  ? uiText(', revised {value1}', { value1: dateLabel(revisedAt, uiText.locale) })
                  : ''}
              </dd>
              <dt>{uiText('Conversation')}</dt>
              <dd>
                {result.threadId ? (
                  <button
                    className="ws-text-button"
                    onClick={() => ws.openThread(result.threadId!)}
                  >
                    {uiText('Open conversation')}
                  </button>
                ) : (
                  uiText('Not recorded')
                )}
              </dd>
              <dt>{uiText('Run')}</dt>
              <dd>
                {lineage.skill ? (
                  <button
                    className="ws-text-button"
                    onClick={() => ws.openCanvas({ kind: 'skill', id: lineage.skill!.id })}
                  >
                    {lineageDetail(lineage, locale)}
                  </button>
                ) : (
                  lineageDetail(lineage, locale)
                )}
              </dd>
              <dt>{uiText('Updates')}</dt>
              <dd>
                {result.maintainedBy
                  ? uiText('Kept up to date by {value1}', {
                      value1: result.maintainedBy.name ?? uiText('an agent'),
                    })
                  : uiText('Version {value1}', {
                      value1: result.revision?.current ?? result.currentRevision ?? 1,
                    })}
              </dd>
            </dl>
          </>
        ) : null}
      </div>
    </div>
  );
}
